import {} from /* DEFAULT_BREATHING_STYLE */ '@/types/breathing';
import { ensureUserProfile } from './auth';
import type { SessionTemplate } from './api';
import { supabase } from './supabase';

export type NewSessionTemplate = Omit<
  SessionTemplate,
  'id' | 'created_at' | 'updated_at' | 'user_id'
> & {
  user_id?: string; // optional if using RLS with auth.uid()
};

export async function listTemplatesForCurrentUser(): Promise<
  SessionTemplate[]
> {
  const { data: userRes } = await supabase.auth.getUser();
  const user = userRes.user;
  if (!user) return [];

  const { data, error } = await supabase
    .from('session_templates')
    .select('*')
    .eq('user_id', user.id)
    .order('template_name', { ascending: true });

  if (error) throw error;
  return (data as SessionTemplate[]) || [];
}

export async function createTemplate(
  input: NewSessionTemplate
): Promise<SessionTemplate> {
  // Ensure a public.users row exists to satisfy FK on session_templates.user_id
  await ensureUserProfile();

  const { data: userRes } = await supabase.auth.getUser();
  const user = userRes.user;
  if (!user) throw new Error('Not authenticated');

  const payload = {
    user_id: user.id,
    template_name: input.template_name,
    voice_guidance: input.voice_guidance,
    phase_chime: input.phase_chime,
    background_sound: input.background_sound,
    music_enabled: input.music_enabled ?? false,
    music_track_id: input.music_enabled ? (input.music_track_id ?? null) : null,
    intro_voice: input.intro_voice,
    duration: input.duration,
    breathing_ratio_id: input.breathing_ratio_id,
    // Support saving explicit custom ratio parts when user chooses "Other"
    breathing_ratio_other_inhale: input.breathing_ratio_other_inhale ?? null,
    breathing_ratio_other_exhale: input.breathing_ratio_other_exhale ?? null,
    breathing_ratio_other_hold_full:
      input.breathing_ratio_other_hold_full ?? null,
    breathing_ratio_other_hold_empty:
      input.breathing_ratio_other_hold_empty ?? null,
    background_videos_id: input.background_videos_id,
    vision_board_id: input.vision_board_id ?? null,
    // The DB schema now uses `breathing_style_id` (UUID FK). Prefer sending
    // the id only to avoid PostgREST schema cache errors if the text
    // `breathing_style` column has been removed. Keep `breathing_style_id`
    // nullable for backward compatibility.
    breathing_style_id: input.breathing_style_id ?? null,
    // Affirmations
    // Persist whether affirmations are enabled and the optional selected
    // affirmation type. If affirmations are disabled, always persist
    // `affirmation_type` as NULL to avoid stale values.
    affirmation: input.affirmation ?? false,
    affirmation_type: input.affirmation
      ? (input.affirmation_type ?? null)
      : null,
    affirmation_mode: input.affirmation
      ? (input.affirmation_mode ?? null)
      : null,
    affirmation_whisper_mode: input.affirmation
      ? (input.affirmation_whisper_mode ?? false)
      : false,
    play_in_background: input.play_in_background ?? false,
    // New column: also persist background_play boolean for templates
    background_play: input.background_play ?? false,
    hide_cycle_count: input.hide_cycle_count ?? false,
    hide_time_left: input.hide_time_left ?? false,
  };

  let { data, error } = await supabase
    .from('session_templates')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    try {
      // eslint-disable-next-line no-console
      console.error('[createTemplate] supabase insert error', {
        payload,
        error,
      });
    } catch (e) {
      // ignore logging failures
    }

    // PostgREST may return PGRST204 when the server's schema cache is stale
    // (e.g. new column added but PostgREST not reloaded). In that case retry
    // the request without `breathing_style`/`breathing_style_id` fields so
    // older deployments continue to work until the DB server is fully
    // migrated and cache refreshed.
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'breathing_style' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      // For older deployments that still expect the `breathing_style` text
      // column, try a fallback by including the text value derived from
      // input if available. Otherwise delete `breathing_style_id` so the
      // server can accept the payload.
      if (input.breathing_style) {
        // include legacy breathing_style text when available
        // (used only for backward-compatibility with older schemas)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fallbackPayload.breathing_style = input.breathing_style;
      } else {
        delete fallbackPayload.breathing_style_id;
      }

      // Also remove affirmation fields for older servers that don't have
      // the columns yet to avoid failing the whole request.
      delete fallbackPayload.affirmation;
      delete fallbackPayload.affirmation_type;

      try {
        const retry = await supabase
          .from('session_templates')
          .insert(fallbackPayload)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[createTemplate] retry without breathing_style failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer `play_in_background` column
    // (e.g. migration applied but server not reloaded), retry without that
    // field so older servers can accept the payload until they are updated.
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'play_in_background' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.play_in_background;

      try {
        const retry = await supabase
          .from('session_templates')
          .insert(fallbackPayload)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[createTemplate] retry without play_in_background failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer `background_play` column
    // (e.g. migration applied but server not reloaded), retry without that
    // field so older servers can accept the payload until they are updated.
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'background_play' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.background_play;

      try {
        const retry = await supabase
          .from('session_templates')
          .insert(fallbackPayload)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[createTemplate] retry without background_play failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer hide UI columns
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      (error.message.includes("Could not find the 'hide_cycle_count' column") ||
        error.message.includes("Could not find the 'hide_time_left' column"))
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.hide_cycle_count;
      delete fallbackPayload.hide_time_left;

      try {
        const retry = await supabase
          .from('session_templates')
          .insert(fallbackPayload)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[createTemplate] retry without hide UI columns failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If we reach here, propagate the original error
    throw error;
  }

  return data as SessionTemplate;
}

export async function updateTemplate(
  id: string,
  input: NewSessionTemplate
): Promise<SessionTemplate> {
  const payload = {
    template_name: input.template_name,
    voice_guidance: input.voice_guidance,
    phase_chime: input.phase_chime,
    background_sound: input.background_sound,
    music_enabled: input.music_enabled ?? false,
    music_track_id: input.music_enabled ? (input.music_track_id ?? null) : null,
    intro_voice: input.intro_voice,
    duration: input.duration,
    breathing_ratio_id: input.breathing_ratio_id,
    // Preserve or update explicit custom ratio fields when present
    breathing_ratio_other_inhale: input.breathing_ratio_other_inhale ?? null,
    breathing_ratio_other_exhale: input.breathing_ratio_other_exhale ?? null,
    breathing_ratio_other_hold_full:
      input.breathing_ratio_other_hold_full ?? null,
    breathing_ratio_other_hold_empty:
      input.breathing_ratio_other_hold_empty ?? null,
    background_videos_id: input.background_videos_id,
    vision_board_id: input.vision_board_id ?? null,
    // Prefer sending the FK id field; avoid sending the text column which
    // may have been removed from the schema in some deployments.
    breathing_style_id: input.breathing_style_id ?? null,
    // Affirmations
    affirmation: input.affirmation ?? false,
    affirmation_type: input.affirmation
      ? (input.affirmation_type ?? null)
      : null,
    affirmation_mode: input.affirmation
      ? (input.affirmation_mode ?? null)
      : null,
    affirmation_whisper_mode: input.affirmation
      ? (input.affirmation_whisper_mode ?? false)
      : false,
    play_in_background: input.play_in_background ?? false,
    // New column: also persist background_play boolean for templates
    background_play: input.background_play ?? false,
    hide_cycle_count: input.hide_cycle_count ?? false,
    hide_time_left: input.hide_time_left ?? false,
  };

  let { data, error } = await supabase
    .from('session_templates')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    try {
      // eslint-disable-next-line no-console
      console.error('[updateTemplate] supabase update error', {
        id,
        payload,
        error,
      });
    } catch (e) {
      // ignore
    }

    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'breathing_style' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      if (input.breathing_style) {
        // Provide the text breathing_style for older schemas as a fallback
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fallbackPayload.breathing_style = input.breathing_style;
      } else {
        // If no textual style is available, remove the id so an older
        // server without the id column can accept the payload.
        delete fallbackPayload.breathing_style_id;
      }

      // Remove affirmation fields too for older servers
      delete fallbackPayload.affirmation;
      delete fallbackPayload.affirmation_type;

      try {
        const retry = await supabase
          .from('session_templates')
          .update(fallbackPayload)
          .eq('id', id)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[updateTemplate] retry without breathing_style failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer `play_in_background` column
    // (e.g. migration applied but server not reloaded), retry without that
    // field so older servers can accept the payload until they are updated.
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'play_in_background' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.play_in_background;

      try {
        const retry = await supabase
          .from('session_templates')
          .update(fallbackPayload)
          .eq('id', id)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[updateTemplate] retry without play_in_background failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer `background_play` column
    // (e.g. migration applied but server not reloaded), retry without that
    // field so older servers can accept the payload until they are updated.
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      error.message.includes("Could not find the 'background_play' column")
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.background_play;

      try {
        const retry = await supabase
          .from('session_templates')
          .update(fallbackPayload)
          .eq('id', id)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[updateTemplate] retry without background_play failed',
          retryErr
        );
        throw retryErr;
      }
    }

    // If PostgREST cache is stale about the newer hide UI columns
    if (
      error.code === 'PGRST204' &&
      typeof error.message === 'string' &&
      (error.message.includes("Could not find the 'hide_cycle_count' column") ||
        error.message.includes("Could not find the 'hide_time_left' column"))
    ) {
      const fallbackPayload = { ...payload } as Record<string, unknown>;
      delete fallbackPayload.hide_cycle_count;
      delete fallbackPayload.hide_time_left;

      try {
        const retry = await supabase
          .from('session_templates')
          .update(fallbackPayload)
          .eq('id', id)
          .select('*')
          .single();
        if (retry.error) throw retry.error;
        return retry.data as SessionTemplate;
      } catch (retryErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[updateTemplate] retry without hide UI columns failed',
          retryErr
        );
        throw retryErr;
      }
    }

    throw error;
  }

  return data as SessionTemplate;
}

export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from('session_templates')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
